// src/router.js
import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Common Pages
import Home from './pages/Home';
import About from './pages/About';
import FAQ from './pages/FAQ';
import NotFound from './pages/NotFound';
import SpotLight from './pages/SpotLight';
import AccountSettingsPage from './pages/AccountSettingsPage';

// Auth Pages
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import UserTypeSelection from './components/auth/UserTypeSelection';
import ResetPassword from './components/auth/ResetPassword';
import AuthRedirectHandler from './components/auth/AuthRedirectHandler'; // Import the new component

// Parent Pages/Components
import ParentDashboard from './components/parent/ParentDashboard';
import ProviderSearch from './components/parent/ProviderSearch';
import JobSeekerSearch from './components/parent/JobSeekerSearch';
import ParentProfilePage from './pages/ParentProfilePage';
import ChildrenManager from './components/parent/ChildrenManager';
import ParentJobListing from './components/parent/ParentJobListing';
import ParentNotificationsPage from './pages/ParentNotificationsPage';
import ParentApplications from './components/parent/ParentApplications';
import ParentWaitlists from './components/parent/ParentWaitlists';

// Provider Pages/Components
import ProviderDashboard from './components/provider/ProviderDashboard';
import SpotListing from './components/provider/SpotListing';
import JobListing from './components/provider/JobListing';
import ProviderProfilePage from './pages/ProviderProfilePage';
import WaitlistManager from './components/provider/WaitlistManager';
import SearchChildren from './components/provider/SearchChildren';
import JobApplications from './components/provider/JobApplications';

// Job Seeker Pages/Components
import JobSeekerDashboard from './components/jobseeker/JobSeekerDashboard';
import JobSearch from './components/jobseeker/JobSearch';
import JobSeekerProfilePage from './pages/JobSeekerProfilePage';
import JobDetailsPage from './pages/JobDetailsPage';
import JobSeekerApplications from './components/jobseeker/JobSeekerApplications';

// Messaging Components
import MessagingCenter from './components/messaging/MessagingCenter';

// Mobile Testing Component
import MobileTestPage from './components/MobileTestPage';

// Chat Page Component
import ChatPage from './components/messaging/ChatPage';

// Protected Route Component
import ProtectedRoute from './components/common/ProtectedRoute';

// Create a wrapper component to include AuthRedirectHandler
const WithAuthRedirectHandler = ({ children }) => (
  <>
    <AuthRedirectHandler />
    {children}
  </>
);

const router = createBrowserRouter([
  // Public Routes
  {
    path: '/',
    element: <WithAuthRedirectHandler><MainLayout /></WithAuthRedirectHandler>,
    children: [
      {
        index: true,
        element: <Home />
      },
      {
        path: 'about',
        element: <About />
      },
      {
        path: 'faq',
        element: <FAQ />
      },
      {
        path: 'mobile-test',
        element: <MobileTestPage />
      },
      {
        path: 'provider/:id',
        element: <ProviderProfilePage />
      },
      {
        path: 'parent/:id',
        element: <ParentProfilePage />
      },
      {
        path: 'jobseeker/:id',
        element: <JobSeekerProfilePage />
      },
      {
        path: 'spotlight',
        element: <SpotLight />
      },
      {
        path: 'opportunity-hub',
        element: <Navigate to="/spotlight" replace />
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  },
  
  // Auth Routes
  {
    path: '/auth',
    element: <WithAuthRedirectHandler><AuthLayout /></WithAuthRedirectHandler>,
    children: [
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'register',
        element: <Register />
      },
      {
        path: 'select-user-type',
        element: <UserTypeSelection />
      },
      {
        path: 'reset-password',
        element: <ResetPassword />
      },
      {
        index: true,
        element: <Navigate to="/auth/login" replace />
      }
    ]
  },
  
  // Messaging Routes (for all authenticated users)
  {
    path: '/messages',
    element: (
      <ProtectedRoute>
        <WithAuthRedirectHandler><MainLayout /></WithAuthRedirectHandler>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <MessagingCenter />
      },
      {
        path: ':conversationId',
        element: <ChatPage />
      }
    ]
  },

  // Account Settings Route (for all authenticated users)
  {
    path: '/account/settings',
    element: (
      <ProtectedRoute>
        <WithAuthRedirectHandler><MainLayout /></WithAuthRedirectHandler>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <AccountSettingsPage />
      }
    ]
  },
  
  // Parent Protected Routes
  {
    path: '/parent',
    element: (
      <ProtectedRoute userType="parents">
        <WithAuthRedirectHandler><MainLayout /></WithAuthRedirectHandler>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <ParentDashboard />
      },
      {
        path: 'search/providers',
        element: <ProviderSearch />
      },
      {
        path: 'search/jobseekers',
        element: <JobSeekerSearch />
      },
      {
        path: 'children',
        element: <ChildrenManager />
      },
      {
        path: 'profile/:id?',
        element: <ParentProfilePage />
      },
      {
        path: 'jobs',
        element: <ParentJobListing />
      },
      {
        path: 'applications',
        element: <ParentApplications />
      },
      {
        path: 'waitlists',
        element: <ParentWaitlists />
      }
    ]
  },
  
  // Provider Protected Routes
  {
    path: '/provider',
    element: (
      <ProtectedRoute userType="providers">
        <WithAuthRedirectHandler><MainLayout /></WithAuthRedirectHandler>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <ProviderDashboard />
      },
      {
        path: 'spots',
        element: <SpotListing />
      },
      {
        path: 'jobs',
        element: <JobListing />
      },
      {
        path: 'waiting-list',
        element: <WaitlistManager />
      },
      {
        path: 'search/children',
        element: <SearchChildren />
      },
      {
        path: 'profile',
        element: <ProviderProfilePage />
      },
      {
        path: 'applications',
        element: <JobApplications />
      }
    ]
  },
  
  // Job Seeker Protected Routes
  {
    path: '/jobseeker',
    element: (
      <ProtectedRoute userType="jobseekers">
        <WithAuthRedirectHandler><MainLayout /></WithAuthRedirectHandler>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <JobSeekerDashboard />
      },
      {
        path: 'jobs',
        element: <JobSearch />
      },
      {
        path: 'profile/:id?',
        element: <JobSeekerProfilePage />
      },
      {
        path: 'jobs/:jobId',
        element: <JobDetailsPage />
      },
      {
        path: 'applications',
        element: <JobSeekerApplications />
      },
      {
        path: 'search/providers',
        element: <ProviderSearch />
      }
    ]
  }
]);

export default router;